// 면적으로 공정 초안(#170·#171·#175) — 그 공정 m² 줄 수량 합(없으면 견적 면적) ÷ (인원 × 1인1일 작업량) × 회사 계수 · 근거를 같이 돌려준다.

import { draftDays } from "./calc";
import type { ProcessCalibration } from "./calibration";
import type { ProcessStandardItem } from "./types";

export interface DraftLine {
  phase_code: string | null;
  unit: string;
  qty: number | null;
}

export type DraftQuantitySource = "lines" | "area";

export interface PhaseDraft {
  phase_code: string;
  item_code: string;
  item_name: string;
  source: string;
  quantity_m2: number;
  quantity_from: DraftQuantitySource | null;
  crew_size: number;
  standard_crew_size: number;
  laborer_size: number;
  factor: number;
  output_per_person_day: number;
  crew_days: number;
  solo_days: number;
  duration_days: number;
  formula: string;
}

export interface PhaseDraftInput {
  items: ProcessStandardItem[];
  lines: DraftLine[];
  areaM2: number | null;
  calibration: ProcessCalibration;
  phase_code: string;
  item_code?: string | null;
}

const M2_UNITS = new Set(["m2", "제곱미터", "평방미터", "sqm"]);

// 단위가 m² 인지 — 대소문자·공백·전각(NFKC: ㎡·m² = m2)을 무시한다.
export const isM2Unit = (unit: string): boolean => M2_UNITS.has(unit.normalize("NFKC").replace(/\s+/g, "").toLowerCase());

const round2 = (n: number): number => Math.round(n * 100) / 100;

// 인원 표시 — 보통인부가 있으면 「3명 — 기능공 2·보통인부 1」, 없으면 「2명」.
export const crewLabel = (crew: number, laborer: number): string =>
  laborer > 0 ? `${crew + laborer}명 — 기능공 ${crew}·보통인부 ${laborer}` : `${crew}명`;

// 1명 기준 일수 앞말 — 보통인부가 있으면 기능공 1명임을 밝힌다.
export const soloLabel = (laborer: number): string => (laborer > 0 ? "기능공 1명이면" : "1명이면");

// 그 공정 m² 줄 수량 합 — 수량이 없거나 0 이하인 줄은 뺀다 · 없으면 null.
export const phaseM2Quantity = (lines: DraftLine[], phaseCode: string): number | null => {
  const sum = lines
    .filter((l) => l.phase_code === phaseCode && isM2Unit(l.unit) && l.qty !== null && Number.isFinite(l.qty) && l.qty > 0)
    .reduce((acc, l) => acc + (l.qty as number), 0);
  const total = round2(sum);
  return total > 0 ? total : null;
};

export const itemsForPhase = (items: ProcessStandardItem[], phaseCode: string): ProcessStandardItem[] =>
  items.filter((i) => i.phase_code === phaseCode).sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));

// 면적·m² 줄이 둘 다 없으면 null — 화면이 버튼을 막고 이유를 보인다.
export const draftQuantityOf = (
  lines: DraftLine[],
  areaM2: number | null,
  phaseCode: string
): { quantity_m2: number; quantity_from: DraftQuantitySource } | null => {
  const fromLines = phaseM2Quantity(lines, phaseCode);
  if (fromLines !== null) return { quantity_m2: fromLines, quantity_from: "lines" };
  if (areaM2 !== null && Number.isFinite(areaM2) && areaM2 > 0) return { quantity_m2: round2(areaM2), quantity_from: "area" };
  return null;
};

const num = (n: number): string => String(round2(n));

// 한 항목·수량·인원·계수로 초안 근거 — 저장해 둔 근거를 다시 보일 때도 같은 식을 쓴다.
export const draftOfItem = (
  item: ProcessStandardItem,
  quantityM2: number,
  crew: number,
  factor: number,
  quantityFrom: DraftQuantitySource | null = null
): PhaseDraft => {
  const days = draftDays({ quantityM2, crew, outputPerPersonDay: item.output_per_person_day, factor });
  const base = `${num(quantityM2)}㎡ ÷ ${num(item.output_per_person_day)}㎡/인·일 × 계수 ${num(factor)}`;
  const laborer = item.laborer_size;
  const crewText = laborer > 0 ? `기능공 ${crew}명` : `${crew}명`;
  const laborerText = laborer > 0 ? `보통인부 ${laborer}명 포함 ${crew + laborer}명 · ` : "";
  return {
    phase_code: item.phase_code,
    item_code: item.code,
    item_name: item.name,
    source: item.source,
    quantity_m2: quantityM2,
    quantity_from: quantityFrom,
    crew_size: crew,
    standard_crew_size: item.crew_size,
    laborer_size: laborer,
    factor,
    output_per_person_day: item.output_per_person_day,
    crew_days: days.crewDays,
    solo_days: days.soloDays,
    duration_days: days.durationDays,
    formula: `${base} ÷ ${crewText} = ${days.crewDays}일 (${laborerText}${soloLabel(laborer)} ${days.soloDays}일 · 0.5일 단위 올림)`,
  };
};

// 고른 시공법(없거나 목록 밖이면 그 공정 첫 항목)으로 한 공정 초안 · 항목이나 수량이 없으면 null.
export const draftPhase = (input: PhaseDraftInput): PhaseDraft | null => {
  const candidates = itemsForPhase(input.items, input.phase_code);
  const item = candidates.find((i) => i.code === input.item_code) ?? candidates[0];
  if (!item) return null;
  const quantity = draftQuantityOf(input.lines, input.areaM2, input.phase_code);
  if (!quantity) return null;
  return draftOfItem(item, quantity.quantity_m2, input.calibration.crewSize ?? item.crew_size, input.calibration.factor, quantity.quantity_from);
};
